import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, getApiError } from "@/lib/api";
import { fmtLog } from "@/lib/tz";
import { toast } from "sonner";
import { ClipboardCheck, Loader2, CheckCircle2, ChevronRight } from "lucide-react";
import { DOCS_ACCENT as ACCENT, LIBRARY_META } from "@/lib/govDocs";

export default function DocumentApprovals() {
  const nav = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.govApprovals().then(setData).catch((e) => { setData({ drafts: [] }); toast.error(getApiError(e, "Couldn't load approvals.")); });
  }, []);

  const mine = (data?.drafts || []).filter((d) => d.needs_my_approval).length;

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="doc-approvals">
      <div className="flex items-center gap-2.5">
        <ClipboardCheck className="h-5 w-5" style={{ color: ACCENT }} />
        <h1 className="text-xl font-semibold font-brand text-slate-800">Approvals</h1>
      </div>
      <p className="text-xs text-slate-500 mt-1 mb-5">
        Drafts submitted for Management Team approval. Every member must approve; once they all have, the draft becomes Active automatically.
        {data && !data.on_team && " You're not on the Management Team, so you can track these but not approve them."}
      </p>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {!data ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
        ) : !data.drafts.length ? (
          <div className="flex items-center justify-center gap-2 text-sm text-slate-400 py-16">
            <CheckCircle2 className="h-4 w-4 text-emerald-500" /> Nothing is waiting for approval.
          </div>
        ) : (
          <>
            {data.on_team && (
              <div className="px-4 py-2.5 border-b border-slate-100 text-xs text-slate-600" style={{ backgroundColor: `${ACCENT}0d` }}>
                {mine ? <><strong>{mine}</strong> draft{mine === 1 ? " needs" : "s need"} your approval.</> : "You're up to date. Nothing needs your approval."}
              </div>
            )}
            <div className="divide-y divide-slate-100">
              {data.drafts.map((d) => (
                <button key={d.document_id} onClick={() => nav(`/documents/${d.library}/${d.document_id}?tab=draft`)}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50/70" data-testid={`approval-row-${d.document_id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-slate-800">
                      {d.code && <span className="text-slate-400 font-normal mr-1.5">{d.code}</span>}{d.title}
                      <span className="text-[11px] text-slate-400 font-normal ml-2">
                        {LIBRARY_META[d.library]?.label} · {d.active_version ? `replaces version ${d.active_version}` : "new document"}
                      </span>
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5">
                      Submitted by {d.submitted_by} on {fmtLog(d.submitted_at)}
                      {!d.complete && d.waiting_on.length > 0 && <> · Waiting on {d.waiting_on.join(", ")}</>}
                    </div>
                  </div>
                  {d.needs_my_approval && <span className="text-[10px] font-medium px-2 py-0.5 rounded-full border bg-violet-50 text-violet-700 border-violet-200 whitespace-nowrap">Your approval needed</span>}
                  <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full border whitespace-nowrap ${d.complete ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-50 text-slate-600 border-slate-200"}`}>
                    {d.complete ? "Ready to make Active" : `${d.approved} of ${d.required} approved`}
                  </span>
                  <ChevronRight className="h-4 w-4 text-slate-300" />
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
